import { useState } from "react";

import { SavedFilterView } from "components/Filters/types";
import { WebhooksTierInfo } from "components/TierInfo/WebhooksTierInfo";
import LinkButton from "ds/components/Button/LinkButton";
import TableSimple from "ds/components/TableSimple";
import useAnalytics from "hooks/useAnalytics";
import useErrorHandle from "hooks/useErrorHandle";
import useTypedContext from "hooks/useTypedContext";
import { SpacesContext } from "views/Account/SpacesProvider";

import { useLayoutActions } from "../../hooks/useLayoutActions";
import { useLayoutHeading } from "../../hooks/useLayoutHeading";
import { FILTERS_ORDER_SETTINGS_KEY } from "./constants";
import WebhooksEmpty from "./Empty";
import FiltersLayout from "./FiltersLayout";
import { WEBHOOKS_TABLE_CELLS } from "./Table/Cells";
import { SORT_CONFIG } from "./Table/constants";
import useWebhooksTableStore from "./Table/useTableStore";
import useWebhooks from "./useWebhooks";

const VIRTUALIZATION_THRESHOLD = 50;

const Webhooks = () => {
  const trackAnalytics = useAnalytics();
  const [currentSavedView, setCurrentSavedView] = useState<SavedFilterView | undefined>(undefined);
  const { hasEntityCreateAccess } = useTypedContext(SpacesContext);

  const {
    webhooks,
    predicates,
    error,
    loadingContent,
    hasRows,
    hasNoResults,
    hasNextPage,
    loadMoreItems,
  } = useWebhooks();

  const ErrorContent = useErrorHandle(error);

  useLayoutHeading("Webhooks");

  useLayoutActions(
    hasEntityCreateAccess ? (
      <LinkButton to="/new/webhook" variant="primary">
        Create webhook
      </LinkButton>
    ) : null
  );

  if (ErrorContent) {
    return ErrorContent;
  }

  return (
    <>
      <WebhooksTierInfo />
      <FiltersLayout
        predicates={predicates}
        currentSavedView={currentSavedView}
        setCurrentSavedView={setCurrentSavedView}
      >
        {(hasRows || loadingContent) && (
          <TableSimple
            data={webhooks}
            loading={loadingContent}
            store={useWebhooksTableStore}
            ariaLabel="Webhooks list"
            trackAnalytics={trackAnalytics}
            cellPerColumn={WEBHOOKS_TABLE_CELLS}
            filtersStorageKey={FILTERS_ORDER_SETTINGS_KEY}
            sortConfig={SORT_CONFIG}
            hasPagination={false}
            onLoadMore={hasNextPage ? loadMoreItems : undefined}
            virtualize={webhooks.length >= VIRTUALIZATION_THRESHOLD}
          />
        )}

        {!hasRows && !loadingContent && <WebhooksEmpty hasNoResults={hasNoResults} />}
      </FiltersLayout>
    </>
  );
};

export default Webhooks;
